import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR } from "../lib/constants";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from "lucide-react";

function Row({ kind, label, amount }) {
  if (kind === "section") {
    return (
      <tr className="bg-emerald-50">
        <td colSpan={2} className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-emerald-900">{label}</td>
      </tr>
    );
  }
  const base = "px-4 py-2 border-b border-slate-100";
  const isBold = kind === "total" || kind === "grand";
  return (
    <tr className={kind === "grand" ? "bg-slate-50" : ""}>
      <td className={`${base} ${isBold ? "font-bold text-slate-900" : "text-slate-600 pl-8"} text-sm`}>{label}</td>
      <td className={`${base} text-right text-sm whitespace-nowrap ${isBold ? "font-bold" : ""} ${kind === "grand" ? "text-emerald-800" : "text-slate-800"}`}>
        {amount === null || amount === undefined ? "" : formatIDR(amount)}
      </td>
    </tr>
  );
}

function buildRows(report, data) {
  if (!data) return [];
  if (report === "lak") {
    const rows = [{ kind: "section", label: "AKTIVITAS OPERASI" }];
    data.operasi.penerimaan.forEach((it) => rows.push({ kind: "item", label: `Penerimaan - ${it.label}`, amount: it.amount }));
    data.operasi.pentasyarufan.forEach((it) => rows.push({ kind: "item", label: `Pentasyarufan - ${it.label}`, amount: -it.amount }));
    rows.push({ kind: "total", label: "Kas Netto dari Aktivitas Operasi", amount: data.operasi.netto });
    rows.push({ kind: "section", label: "AKTIVITAS INVESTASI" });
    data.investasi.items.forEach((it) => rows.push({ kind: "item", label: `Perolehan Aset - ${it.label}`, amount: it.amount }));
    rows.push({ kind: "total", label: "Kas Netto dari Aktivitas Investasi", amount: data.investasi.total });
    rows.push({ kind: "section", label: "AKTIVITAS PENDANAAN" });
    rows.push({ kind: "total", label: "Kas Netto dari Aktivitas Pendanaan", amount: data.pendanaan });
    rows.push({ kind: "total", label: "Kenaikan (Penurunan) Kas", amount: data.kenaikan_kas });
    rows.push({ kind: "grand", label: "KAS AWAL PERIODE", amount: data.kas_awal });
    rows.push({ kind: "grand", label: "KAS AKHIR PERIODE", amount: data.kas_akhir });
    return rows;
  }
  const rows = [{ kind: "grand", label: "SALDO AWAL DANA", amount: data.saldo_awal }];
  rows.push({ kind: "section", label: "PENERIMAAN" });
  data.penerimaan.forEach((it) => rows.push({ kind: "item", label: it.label, amount: it.amount }));
  rows.push({ kind: "total", label: "Total Penerimaan", amount: data.total_penerimaan });
  rows.push({ kind: "section", label: "PENTASYARUFAN" });
  data.pentasyarufan.forEach((it) => rows.push({ kind: "item", label: it.label, amount: it.amount }));
  rows.push({ kind: "total", label: "Total Pentasyarufan", amount: data.total_pentasyarufan });
  rows.push({ kind: "total", label: "Kenaikan (Penurunan) Dana", amount: data.surplus });
  rows.push({ kind: "grand", label: "SALDO AKHIR DANA", amount: data.saldo_akhir });
  return rows;
}

export default function LaporanVerify() {
  const [params] = useSearchParams();
  const report = params.get("report") || "";
  const start = params.get("start") || "";
  const end = params.get("end") || "";
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get("/public/report-verify", { params: { report, start, end } })
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [report, start, end]);

  const rows = data ? buildRows(data.report, data.data) : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="laporan-verify-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/">
            <img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" />
          </Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Verifikasi Laporan</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Kecamatan Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="laporan-verify-back">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Beranda
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        {!data && !notFound && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-800" />
          </div>
        )}

        {notFound && (
          <Card>
            <CardContent className="p-10 text-center" data-testid="laporan-invalid">
              <XCircle className="mx-auto mb-4 h-14 w-14 text-red-500" />
              <h1 className="font-heading text-xl font-bold text-slate-900">Laporan Tidak Ditemukan</h1>
              <p className="mt-2 text-sm text-slate-500">Tautan verifikasi tidak valid.</p>
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="laporan-valid">
              <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-700" />
              <div>
                <p className="text-sm font-bold text-emerald-900">Laporan Terverifikasi Asli</p>
                <p className="text-xs text-emerald-700">Dokumen ini diterbitkan oleh Sistem Informasi LAZISNU Dawe</p>
              </div>
            </div>
            <Card data-testid="laporan-verify-content">
              <CardContent className="p-0">
                <div className="border-b border-slate-100 px-4 py-4 text-center">
                  <h1 className="font-heading text-lg font-bold text-slate-900">{data.title}</h1>
                  <p className="text-xs text-slate-500">Periode {data.start} s.d. {data.end} · LAZISNU Dawe · PSAK 109</p>
                </div>
                <table className="w-full" data-testid="laporan-verify-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Uraian</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <Row key={i} kind={r.kind} label={r.label} amount={r.amount} />
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
